import {Extension, type Range} from '@tiptap/core';
import {ReactRenderer} from '@tiptap/react';
import Suggestion from '@tiptap/suggestion';
import {Client4} from 'mattermost-redux/client';
import {Plugin, PluginKey} from 'prosemirror-state';

import {Constants} from 'utils/constants';
import {SearchTimeMeasurer, sendToStatist} from '@time-webkit/statist';
import {type SpecialMentions} from 'features/editor/components/editor/helpers';

import {type MentionOptions} from '@tiptap/extension-mention';

import {UserMentionsList} from './components/mention-list';
import {
    MAX_RESULT_COUNT,
    USER_RESULT_LIMIT,
    GROUP_RESULT_LIMIT,
} from './constants';
import {transformGroupsResponse, transformUsersAutocompleteResponse} from './transformers';
import {findSuggestionMatch} from './find_suggestion_match';
import type {Mention} from './types';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        mention: {

            /**
             * Start searching for a mention
             */
            startMention: () => ReturnType;

            /**
             * Mark text in a range as a mention
             */
            markAsMention: (range: Range, id: string, postfix?: string) => ReturnType;
        };
    }
}

const emptyMentions: any[] = [];

const suggestionKey = new PluginKey('suggestion-timeEditorMentionExtension');

const clearMentionMarkPluginKey = new PluginKey('clearMentionMark');

const searchTimeMeasurer = new SearchTimeMeasurer('editor.searchUser.result');

type Storage = {
    abortController: AbortController | undefined | null;
    menu: ReactRenderer | null;
    hasButtonBeenPressed: boolean;
    mentions: Mention[];
    query?: string;
    queryWithoutResults?: string;
}

const NUM_OR_ENG_LETTER_REGEX = /[0-9a-zA-Z]/;

export const TimeEditorMentionExtension = Extension.create<
{
    channelId: string;
    teamId: string;
    predefinedMentions: SpecialMentions;
    token?: string;
    withinThread: boolean;
    withinDirect: boolean;
    useGroupMentions: boolean;
    disableInExtensions?: Set<string>;
    disableInMarks?: Set<string>;
} & MentionOptions, Storage>({
    name: 'timeEditorMentionExtension',

    addStorage() {
        return {
            abortController: null,
            menu: null,
            hasButtonBeenPressed: false,
            mentions: [],
            query: undefined,
            queryWithoutResults: undefined,
        };
    },

    addCommands() {
        return {
            startMention:
                () =>
                    ({chain, state}) => {
                        this.storage.hasButtonBeenPressed = true;

                        const parentOffset = state.selection.$from.parentOffset;
                        const isOnStart = parentOffset === 0;

                        let content = '@';

                        if (!isOnStart) {
                            const prevChar = state.selection.$from.parent.textBetween(parentOffset - 1, parentOffset);

                            if (prevChar === '@') {
                                return chain().focus().run();
                            }

                            if (prevChar !== ' ') {
                                content = ` ${content}`;
                            }
                        }

                        return chain().insertContent(content).focus().run();
                    },
            markAsMention: (range, id, postfix = ' ') =>
                ({editor, chain}) => {
                    const nodeAfter = editor.view.state.selection.$to.nodeAfter;
                    const overrideSpace = nodeAfter?.text?.startsWith(' ');

                    if (overrideSpace) {
                        range.to += 1;
                    }

                    const mentionText = `@${id}`;
                    try {
                        chain()
                            .focus()
                            .unsetMark('mention', {extendEmptyMarkRange: true})
                            .insertContentAt(range, mentionText + postfix)
                            .setTextSelection({from: range.from, to: range.from + mentionText.length})
                            .setMark('mention', {id})
                            .unsetMark('link')
                            .setTextSelection(range.from + mentionText.length + 1)
                            .unsetMark('mention')
                            .run();

                        window.getSelection()?.collapseToEnd();

                    // eslint-disable-next-line no-empty
                    } catch {
                        return false;
                    }

                    return true;
                },
        };
    },

    addProseMirrorPlugins() {
        const options = this.options;
        const storage = this.storage;

        const getMentions = async (query: string) => {
            const prevAbortController = storage?.abortController;

            prevAbortController?.abort();

            if (typeof query === 'undefined' ||
                (storage.queryWithoutResults && query.startsWith(storage.queryWithoutResults))
            ) {
                storage.mentions = emptyMentions;
                return emptyMentions;
            }

            searchTimeMeasurer.measure(query);
            const controller = new AbortController();

            storage.abortController = controller;

            const data = await Promise.all([
                Client4.autocompleteUsers(
                    query,
                    options.teamId,
                    options.channelId,
                    {
                        limit: USER_RESULT_LIMIT,
                        boost: true,
                    },
                    controller.signal,
                )
                    .then(transformUsersAutocompleteResponse),
                options.useGroupMentions ? Client4.getGroups(
                    true,
                    0,
                    GROUP_RESULT_LIMIT,
                    false,
                    false,
                    'custom',
                    query,
                    controller.signal,
                ).then(transformGroupsResponse) : Promise.resolve([]),
            ]);

            const [transformedUsers = {users: [], bots: []}, groups = []] = data;
            const {users, bots} = transformedUsers;
            const dataMentions = users.concat(groups, bots);

            let predefinedMentions: Mention[];
            if (this.options.withinDirect || !this.options.predefinedMentions) {
                predefinedMentions = [];
            } else if (query) {
                const {here, channel, all} = this.options.predefinedMentions;
                predefinedMentions = [here, channel, all].filter(({handle}) => handle?.includes(query));
            } else {
                const {here, all} = this.options.predefinedMentions;
                predefinedMentions = [here, all];
            }

            searchTimeMeasurer.measure(query, dataMentions.length);

            storage.mentions = predefinedMentions.concat(dataMentions);
            storage.queryWithoutResults = storage.mentions.length === 0 ? query : undefined;

            return storage.mentions.slice(0, MAX_RESULT_COUNT);
        };

        return [
            // eslint-disable-next-line new-cap
            Suggestion({
                editor: this.editor,
                pluginKey: suggestionKey,
                char: '@',
                allowSpaces: true,
                allowedPrefixes: ['', ' '],
                command: ({editor, range, props}) => editor.commands.markAsMention(range, props.id),
                allow: ({state}) => {
                    const parentName = state.selection.$from.parent.type.name;
                    const marks = state.selection.$from.marks();

                    if (options.disableInExtensions?.has(parentName)) {
                        return false;
                    }

                    if (options.disableInMarks) {
                        return !marks.some((mark) => options.disableInMarks!.has(mark.type.name));
                    }

                    return true;
                },
                render() {
                    return {
                        onBeforeStart(props) {
                            storage.query = props.query;
                            storage.mentions = [];
                        },
                        onStart(props) {
                            sendToStatist('editor.searchUser.tap', {
                                sourceTap: options.withinThread ? 'thread' : 'post',
                                sourceSearch: storage.hasButtonBeenPressed ? 'button' : 'type',
                            });
                            storage.hasButtonBeenPressed = false;
                            const component = new ReactRenderer(UserMentionsList, {
                                editor: props.editor,
                                props: {
                                    ...props,
                                    getMentions,
                                },
                            });

                            document.body.appendChild(component.element);

                            storage.menu = component;
                        },
                        onUpdate(props) {
                            const lastChar = props.query[props.query.length - 1] ?? '';
                            const isQueryExtendedByChar = props.query === `${storage.query}${lastChar}`;
                            const checkMentions = isQueryExtendedByChar && !NUM_OR_ENG_LETTER_REGEX.test(lastChar);

                            if (checkMentions) {
                                const mention = storage.mentions.find((m) => m.handle === storage.query);

                                if (mention) {
                                    props.editor.commands.markAsMention(props.range, mention.handle, lastChar);
                                    return;
                                }
                            }

                            storage.query = props.query;

                            const component = storage.menu as ReactRenderer;
                            const hideLoader = Boolean(
                                storage.queryWithoutResults && props.query.startsWith(storage.queryWithoutResults),
                            );

                            component?.updateProps({
                                ...props,
                                getMentions,
                                hideLoader,
                            });
                        },
                        onKeyDown(props) {
                            const component = storage.menu;

                            if (props.event.key === Constants.KeyCodes.ESCAPE[0] && component) {
                                if (component.element) {
                                    document.body.removeChild(component.element);
                                }
                                component.destroy();
                                storage.menu = null;

                                return true;
                            }

                            return false;
                        },
                        onExit() {
                            const component = storage.menu as ReactRenderer;
                            if (component?.element) {
                                document.body.removeChild(component?.element);
                            }
                            component?.destroy();
                            storage.menu = null;
                            storage.query = undefined;
                            storage.queryWithoutResults = undefined;
                            storage.mentions = [];
                            searchTimeMeasurer.dispose();
                        },
                    };
                },
                findSuggestionMatch,
            }),
            new Plugin({
                key: clearMentionMarkPluginKey,
                appendTransaction: (transactions, oldState, newState) => {
                    const docChanges =
                        transactions.some((transaction) => transaction.docChanged) && !oldState.doc.eq(newState.doc);

                    if (!docChanges) {
                        return null;
                    }

                    const tr = newState.tr;

                    const from = Math.min(oldState.selection.from, newState.selection.from) - 1;
                    const to = Math.min(
                        Math.max(oldState.selection.to, newState.selection.to) + 1,
                        newState.doc.content.size,
                    );

                    newState.doc.nodesBetween(from, to, (node, pos) => {
                        const mentionMark = node.marks.find((mark) => mark.type.name === 'mention');
                        if (mentionMark && node.text !== `@${mentionMark.attrs.id}`) {
                            tr.removeMark(pos, pos + node.nodeSize, mentionMark.type);
                        }
                    });

                    if (!tr.steps.length) {
                        return null;
                    }

                    return tr;
                },
            }),
        ];
    },
});
